import { ampli } from '@/ampli';
import Link from 'next/link';
import { useRef } from 'react';

import { NETWORK_NAME_PER_CHAIN_ID, solana } from '@/constants/chain';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { CheckCircleFilledIcon } from '@/ui/Icons/CheckCircleFilledIcon';
import { SolidCopyIcon } from '@/ui/Icons/SolidCopyIcon';

import { tradeRoute } from '@/utils/url';

import { fontStyles } from '../../../../styles/typography';
import { formatShortAddress } from '../../../../utils/address';
import { classNames } from '../../../../utils/classnames';
import { DiscoveryTokenResult } from '../../../../utils/discovery/types';
import { isTokenAddressNativeAsset } from '../../../../utils/multichain';

interface UnifiedChildTokenNameCellProps {
  item: DiscoveryTokenResult;
}

/**
 * UnifiedChildTokenNameCell - Displays child token information in expanded asset rows
 *
 * Shows the chain name with the token address and copy button on hover.
 */
export function UnifiedChildTokenNameCell({ item }: UnifiedChildTokenNameCellProps) {
  const displayTimer = useRef<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (!item || !item.token) {
    return null;
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (displayTimer.current) {
      clearTimeout(displayTimer.current);
    }

    ampli.tokenAddressCopied({
      token_address: item.token.address,
      token_chain_id: item.token.chainId,
      token_chain_name: NETWORK_NAME_PER_CHAIN_ID[item.token.chainId] || '',
    });
    navigator.clipboard.writeText(item.token.address);
    displayTimer.current = setTimeout(() => {
      displayTimer.current = null;
      if (buttonRef.current) {
        buttonRef.current.dataset.copied = 'false';
      }
    }, 2000);
    if (buttonRef.current) {
      buttonRef.current.dataset.copied = 'true';
    }
  };

  const chainName = NETWORK_NAME_PER_CHAIN_ID[item.token.chainId] || 'Unknown';
  const isNativeToken = isTokenAddressNativeAsset(item.token.address, item.token.chainId);

  return (
    <Link className="flex min-w-0 items-center gap-1.5" href={tradeRoute(item.token)}>
      <ChainAvatar size={20} chainId={item.token.chainId} alt={chainName} />
      {/* Chain name with hover effect to show address (non-native tokens only) */}
      <div
        className={classNames(
          'relative flex items-center overflow-hidden h-4 w-[95px] mobile:w-[118px]',
        )}
      >
        {isNativeToken ? (
          <span
            className={fontStyles({
              variant: 'BODY_MD',
              class: ['leading-4', 'text-token-gray-800'],
            })}
          >
            {chainName}
          </span>
        ) : (
          <div
            className={classNames(
              'transition-transform',
              'duration-200',
              'absolute',
              'top-0',
              'left-0',
              'translate-y-0',
              'group-hover:-translate-y-5',
              'group-focus-within:-translate-y-5',
              'ease-out',
              'flex',
              'flex-col',
              'gap-1',
            )}
          >
            {/* Chain name visible by default */}
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: [
                  'leading-4',
                  'text-token-gray-800',
                  'opacity-100',
                  'group-hover:opacity-0',
                  'group-focus-within:opacity-0',
                  'transition-opacity',
                  'duration-200',
                  'ease-out',
                ],
              })}
            >
              {chainName}
            </span>
            {/* Address visible on hover */}
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: [
                  'leading-4',
                  'text-token-gray-800',
                  'opacity-0',
                  'group-hover:opacity-100',
                  'group-focus-within:opacity-100',
                  'transition-opacity',
                  'duration-200',
                  'ease-out',
                  'flex',
                  'items-center',
                ],
              })}
            >
              {formatShortAddress(item.token.address, item.token.chainId === solana.id ? 4 : 6, 4)}
              <button
                ref={buttonRef}
                type="button"
                data-copied={false}
                onClick={handleCopy}
                aria-label="Copy token address"
                className="z-50 text-token-gray-600 hover:text-token-gray-700 transition-colors ease-out duration-150 relative h-4 w-4 group ml-1"
              >
                <SolidCopyIcon
                  className="group-data-[copied=true]:opacity-0 opacity-100 absolute transition-opacity top-0 left-0 duration-200 ease-out"
                  height={16}
                  width={16}
                />
                <CheckCircleFilledIcon className="group-data-[copied=true]:opacity-100 opacity-0 absolute transition-opacity top-0 left-0 duration-200 ease-out" />
              </button>
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}
